import { EXTENSION_PRIVATE_CHAIN_IDS } from '@web/constants/extensionChains'
import { ModeEnum, type Mode } from '@web/contexts/appModeContext'

/**
 * Public → private (extension): align filter with supported private chains.
 *
 * Phase 3.6 of RAILGUN_HYBRID_REWRITE_PLAN.md — preserve the user's chain
 * selection across the mode flip whenever it makes sense:
 *  - `null` (All) in public stays `null` (All) in private. The offscreen
 *    activates every private-supported chain in parallel at engine boot, so
 *    showing the cross-chain aggregate is no slower than locking to one chain.
 *  - A private-supported chain (Ethereum / Arbitrum / BNB) is preserved.
 *  - A non-private chain (Polygon, Optimism, …) or a virtual filter
 *    (`rewards`, `gasTank`) falls back to `firstPrivateSupportedChainId`.
 *
 * Returns `undefined` when no dispatch is needed (the filter is already correct
 * for private mode — including the `null`/All case).
 */
export function resolveExtensionPrivateFilterWhenEnteringFromPublic(
  currentFilter: bigint | string | null,
  firstPrivateSupportedChainId: string
): string | null | undefined {
  if (currentFilter == null) {
    return undefined
  }

  if (currentFilter === 'rewards' || currentFilter === 'gasTank') {
    return firstPrivateSupportedChainId
  }

  const chainIdNum = Number(currentFilter)
  if (Number.isNaN(chainIdNum) || !EXTENSION_PRIVATE_CHAIN_IDS.includes(chainIdNum)) {
    return firstPrivateSupportedChainId
  }

  return undefined
}

/**
 * Extension + private mode: fix invalid chain only (not rewards/gasTank).
 * `null` means all supported private networks — do not coerce to a single chain.
 * Used when mode became private without going through the tab handler (e.g. URL).
 */
export function resolveExtensionPrivateInvariantFilter(
  currentFilter: bigint | string | null,
  firstPrivateSupportedChainId: string
): string | undefined {
  if (currentFilter === 'rewards' || currentFilter === 'gasTank') {
    return undefined
  }

  if (currentFilter == null) {
    return undefined
  }

  const chainIdNum = Number(currentFilter)
  if (Number.isNaN(chainIdNum) || !EXTENSION_PRIVATE_CHAIN_IDS.includes(chainIdNum)) {
    return firstPrivateSupportedChainId
  }

  return undefined
}

/**
 * Dashboard network filter to apply before switching mode (`undefined` = no dispatch).
 */
export function resolveDashboardNetworkFilterForModeChange(args: {
  previousMode: Mode
  nextMode: Mode
  currentFilter: bigint | string | null
  isExtension: boolean
  firstPrivateSupportedChainId: string | undefined
}): bigint | string | null | undefined {
  const { previousMode, nextMode, currentFilter, isExtension, firstPrivateSupportedChainId } = args

  if (nextMode === ModeEnum.PUBLIC && previousMode === ModeEnum.PRIVATE) {
    if (currentFilter == null) return undefined
    return null
  }

  if (
    nextMode === ModeEnum.PRIVATE &&
    previousMode === ModeEnum.PUBLIC &&
    isExtension &&
    firstPrivateSupportedChainId
  ) {
    return resolveExtensionPrivateFilterWhenEnteringFromPublic(
      currentFilter,
      firstPrivateSupportedChainId
    )
  }

  return undefined
}
